import { Box, Button, Grid } from "@mui/material";
import FormikControl from "../../bmr/components/common/FormikControl";
import { useFormik } from "formik";
import { useMemo } from "react";
import { useSelector } from "react-redux";

const AssignedFormFilters = ({
  onApply,
  onClear,
  initialFilters = {},
  productData = [],
  phaseData = [],
}) => {

  const { templateDropdownlist } = useSelector((state) => state?.bmrFbl);

  const versionOptions = useMemo(() => {
    const seen = new Set();
    return (templateDropdownlist ?? [])
      .flatMap((t) => t.versions ?? [])
      .reduce((acc, v) => {
        if (!seen.has(v.version)) {
          seen.add(v.version);
          acc.push({ value: v.version, label: `V. ${v.version}` });
        }
        return acc;
      }, [])
      .sort((a, b) => a.value - b.value);
  }, [templateDropdownlist]);

  // ── Formik ────────────────────────────────────────────────────────────────
  const formik = useFormik({
    initialValues: {
      product_id: "",
      phase_id: "",
      status: "",
      version: "",
      ...initialFilters,
    },
    enableReinitialize: true,
    onSubmit: (values) => {
      const enriched = { ...values };

      if (values.product_id) {
        const match = productData.find((o) => o.value === values.product_id);
        if (match) enriched.product_id_label = match.label;
      }

      if (values.phase_id) {
        const match = phaseData.find((o) => o.value === values.phase_id);
        if (match) enriched.phase_id_label = match.label;
      }

      if (values.version) {
        enriched.version_label = `V. ${values.version}`;
      }

      if (values.status !== "") {
        enriched.status_label = values.status === "1" ? "Active" : "Inactive";
      }

      onApply(enriched);
    },
  });

  const filteredPhaseOptions = useMemo(() =>
    formik.values.product_id
      ? phaseData.filter((p) => p.productId === formik.values.product_id)
      : phaseData,
  [formik.values.product_id, phaseData]);

  const handleClear = () => {
    formik.resetForm();
    onApply({});   // triggers API call with no filters so table shows all data
    onClear?.();   // closes the popover
  };

  // ================= FORM OPTIONS =================

  const formOptions = [
    {
      label: "Product",
      type: "select",
      name: "product_id",
      placeholder: "All Products",
      options: [
        { label: "All Products", value: "" },
        ...productData,
      ],
    },
    {
      label: "Phase",
      type: "select",
      name: "phase_id",
      placeholder: "All Phases",
      options: [
        { label: "All Phases", value: "" },
        ...filteredPhaseOptions,
      ],
    },
    {
      label: "Status",
      type: "select",
      name: "status",
      placeholder: "All",
      options: [
        { label: "All", value: "" },
        { label: "Active", value: "1" },
        { label: "Inactive", value: "0" },
      ],
    },
    {
      label: "Version",
      type: "select",
      name: "version",
      placeholder: "All Versions",
      options: [
        { label: "All Versions", value: "" },
        ...versionOptions,
      ],
    },
  ];

  return (
    <Box>
      <form onSubmit={formik.handleSubmit}>
        <Grid container spacing={2}>
          {formOptions.map((props, key) => (
            <Grid key={key} item xs={12}>
              <FormikControl formik={formik} {...props} />
            </Grid>
          ))}
        </Grid>

        {/* Actions */}
        <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
          <Button
            size="large"
            variant="outlined"
            sx={{ borderRadius: "6px", flex: 1 }}
            onClick={handleClear}
          >
            Clear
          </Button>
          <Button
            size="large"
            type="submit"
            variant="contained"
            sx={{ borderRadius: "6px", flex: 1 }}
          >
            Apply
          </Button>
        </Box>
      </form>
    </Box>
  );
};

export default AssignedFormFilters;